// Current user / shop session
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AccountKind, Role } from '@vegapart/shared';
import { api } from './api';
import type { Theme } from './theme';

export interface Me {
  user: { id: number; name: string; email: string; is_platform_admin: boolean; language: string | null; ui_theme?: Theme | null };
  tenant_id: string | null;
  member_id: number | null;
  role: Role | null;
  settings: {
    shop_name: string; phone: string | null; address: string | null; currency: string; country: string; language: string;
    timezone: string; default_labor_rate_cents: number; default_tax_rate: number; wo_prefix: string;
    kind: AccountKind; self_customer_id: number | null; modules: AccountKind[]; require_mfa?: boolean;
    auto_pm_reminders?: boolean; appointment_reminders?: boolean; reminder_channel?: 'sms' | 'email';
  } | null;
  mfa?: { enabled: boolean; session_verified: boolean; required: boolean };
  billing?: { access: 'full' | 'read_only'; warning: 'trial_ending' | 'past_due' | null; trial_ends_at: string | null } | null;
  memberships: { member_id: number; role: Role; tenant_id: string; tenant_name: string; kind: AccountKind; modules: AccountKind[] }[];
}

const Ctx = createContext<Me | null>(null);
export const MeProvider = ({ me, children }: { me: Me; children: ReactNode }) => <Ctx.Provider value={me}>{children}</Ctx.Provider>;

export function useMe() {
  const me = useContext(Ctx);
  if (!me) throw new Error('useMe outside MeProvider');
  const isStaff = me.role === 'admin' || me.role === 'advisor';
  const kind = me.settings!.kind;
  const modules = me.settings!.modules ?? [kind];
  const has = (m: AccountKind) => modules.includes(m);
  const asOperator = me.role === 'owner_operator';
  return {
    ...me, settings: me.settings!, isStaff, isAdmin: me.role === 'admin', isMechanic: me.role === 'mechanic',
    kind, modules, has,
    /** Repair-shop package: customers, invoices, work orders for other companies' trucks */
    isShop: has('shop') && !asOperator,
    /** Fleet package: the company's own trucks */
    isFleet: has('fleet') && !asOperator,
    /** Owner-operator view: an owner-operator account, or an owner-operator inside a bigger company */
    isOwnerOp: asOperator || (has('owner_operator') && !has('fleet') && !has('shop')),
    /** Receipts, costs, DOT compliance */
    fleetFeatures: has('fleet') || has('owner_operator'),
    asOperator,
    /** admin / advisor / mechanic */
    isCrew: me.role === 'admin' || me.role === 'advisor' || me.role === 'mechanic',
    /** A unit of the company's own fleet (not a shop customer's truck) */
    ownUnit: (u: { customer_id: number }) => !has('shop') || u.customer_id === me.settings!.self_customer_id
  };
}

export const useMeQuery = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/auth/me'), retry: false, staleTime: 60_000, refetchOnWindowFocus: false });

export interface Member { id: number; name: string; role: Role; active: boolean; email?: string; phone?: string | null; hourly_rate_cents?: number; commission_pct?: number }
export const useMembers = (enabled = true) => useQuery({ queryKey: ['members'], queryFn: () => api.get<Member[]>('/members'), staleTime: 60_000, enabled });

export const woNumber = (prefix: string, n: number) => `${prefix}${n}`;
